import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CATEGORIES, CATEGORY_LABEL, type DocumentCategory } from '@/pages/documents/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export interface EligibleOwner {
  id: string;
  fullName: string;
  email: string;
}

interface Props {
  /** Empleados disponibles en el selector. Si `fixedOwner` viene seteado se ignora. */
  users?: EligibleOwner[];
  /** Si presente, fija el destinatario (no se puede cambiar). */
  fixedOwner?: { id: string; fullName: string };
  onClose: () => void;
  onUploaded: () => void;
}

export function DocumentUploadModal({ users, fixedOwner, onClose, onUploaded }: Props) {
  const [ownerUserId, setOwnerUserId] = useState(fixedOwner?.id ?? '');
  const [category, setCategory] = useState<DocumentCategory>('PAYROLL');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [periodMonth, setPeriodMonth] = useState<string>('');
  const [periodYear, setPeriodYear] = useState<string>('');

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Selecciona un archivo');
      const form = new FormData();
      form.append('file', file);
      form.append('ownerUserId', ownerUserId);
      form.append('category', category);
      form.append('title', title.trim() || file.name);
      if (periodMonth) form.append('periodMonth', periodMonth);
      if (periodYear) form.append('periodYear', periodYear);
      const res = await fetch(`${API_BASE_URL}/admin/documents`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const data: { message?: string | string[] } = await res.json().catch(() => ({}));
        const msg = Array.isArray(data.message) ? data.message[0] : data.message;
        throw new Error(msg ?? `Error ${res.status}`);
      }
      return res.json();
    },
    onSuccess: onUploaded,
  });

  const canSubmit = ownerUserId && title.trim() && file && !upload.isPending;
  const errMsg = upload.error ? (upload.error as Error).message : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        aria-hidden
        onClick={() => !upload.isPending && onClose()}
        className="absolute inset-0"
        style={{ background: 'rgba(10,14,31,0.4)', backdropFilter: 'blur(2px)' }}
      />
      <div className="relative w-full max-w-[560px] max-h-[90vh] bg-card rounded-sm shadow-lg overflow-hidden flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-[15px] font-semibold tracking-tightish">Subir documento</h2>
            <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
              PDF, imagen u Office. Máx 25 MB.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            disabled={upload.isPending}
            className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-line-strong bg-card text-foreground hover:border-foreground transition-colors disabled:opacity-40"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>
              Empleado <span className="text-destructive">*</span>
            </Label>
            {fixedOwner ? (
              <p className="font-mono text-[12.5px] px-3 py-2 rounded-sm bg-paper border border-line-strong text-foreground">
                {fixedOwner.fullName}
              </p>
            ) : (
              <select
                value={ownerUserId}
                onChange={(e) => setOwnerUserId(e.target.value)}
                className="h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none"
              >
                <option value="">— Selecciona —</option>
                {(users ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName} ({u.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>
                Categoría <span className="text-destructive">*</span>
              </Label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as DocumentCategory)}
                className="h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="doc-title">
                Título <span className="text-destructive">*</span>
              </Label>
              <Input
                id="doc-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Nómina abril 2026"
                maxLength={200}
              />
            </div>
          </div>

          {category === 'PAYROLL' ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="doc-month">Mes (1-12)</Label>
                <Input
                  id="doc-month"
                  type="number"
                  min={1}
                  max={12}
                  value={periodMonth}
                  onChange={(e) => setPeriodMonth(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="doc-year">Año</Label>
                <Input
                  id="doc-year"
                  type="number"
                  min={2000}
                  max={2100}
                  value={periodYear}
                  onChange={(e) => setPeriodYear(e.target.value)}
                />
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="doc-file">
              Archivo <span className="text-destructive">*</span>
            </Label>
            <input
              id="doc-file"
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-[13px] file:mr-3 file:h-9 file:px-3 file:rounded-sm file:border file:border-line-strong file:bg-paper file:font-medium file:text-foreground hover:file:bg-card"
            />
            {file ? (
              <p className="font-mono text-[11px] text-muted-foreground">
                {file.name} · {formatSize(file.size)}
              </p>
            ) : null}
          </div>

          {errMsg ? (
            <p role="alert" className="text-sm text-destructive">
              {errMsg}
            </p>
          ) : null}
        </div>

        <footer className="flex justify-end gap-2 px-6 py-3.5 border-t border-border bg-paper">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={upload.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => upload.mutate()}
            disabled={!canSubmit}
          >
            {upload.isPending ? 'Subiendo…' : 'Subir documento'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
